/**
 * Treppan Academy - Certificate Verification
 * Public page to verify certificate authenticity via QR code
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Award, CheckCircle, XCircle, GraduationCap, Calendar, 
  User, BookOpen, Trophy, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  gold: '#D4AF37',
  success: '#10B981',
  fail: '#EF4444',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

export default function CertificateVerify() {
  const { certificateNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyCertificate();
  }, [certificateNumber]);

  const verifyCertificate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/verify/${certificateNumber}`);
      const result = await res.json();
      
      if (result.success && result.valid) {
        setCertificate(result.certificate);
      } else {
        setError(result.message || 'Certificate not found');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to verify certificate');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Verifying certificate...</p>
        </div>
      </div>
    );
  }

  const isDistinction = certificate?.grade === 'distinction';
  const accentColor = isDistinction ? colors.gold : colors.success;

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: colors.background }}>
      <div 
        className="max-w-lg w-full rounded-xl overflow-hidden"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        {/* Header */}
        <div 
          className="p-6 text-center"
          style={{ 
            background: certificate 
              ? `linear-gradient(135deg, ${accentColor}20 0%, transparent 50%)`
              : `linear-gradient(135deg, ${colors.fail}10 0%, transparent 50%)`
          }}
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <GraduationCap className="h-6 w-6" style={{ color: colors.gold }} />
            <span 
              className="font-semibold tracking-wider"
              style={{ color: colors.gold, fontFamily: 'Outfit, sans-serif' }}
            >
              TREPPAN ACADEMY
            </span>
          </div>
          
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Certificate Verification
          </h1>
        </div>

        {/* Content */}
        <div className="p-8">
          {certificate ? (
            <>
              {/* Valid Badge */}
              <div className="flex justify-center mb-6">
                <div 
                  className="flex items-center gap-2 px-4 py-2 rounded-full"
                  style={{ backgroundColor: `${colors.success}20`, border: `1px solid ${colors.success}30` }}
                >
                  <CheckCircle className="h-5 w-5" style={{ color: colors.success }} />
                  <span className="font-semibold" style={{ color: colors.success }}>
                    VERIFIED AUTHENTIC
                  </span>
                </div>
              </div>

              {/* Certificate Details */}
              <div className="space-y-4">
                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <User className="h-5 w-5" style={{ color: colors.textMuted }} />
                    <span className="text-sm uppercase tracking-wider" style={{ color: colors.textMuted }}>
                      Recipient
                    </span>
                  </div>
                  <p 
                    className="text-xl font-semibold"
                    style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
                  >
                    {certificate.user_name}
                  </p>
                </div>

                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <BookOpen className="h-5 w-5" style={{ color: colors.textMuted }} />
                    <span className="text-sm uppercase tracking-wider" style={{ color: colors.textMuted }}>
                      Course
                    </span>
                  </div>
                  <p 
                    className="text-xl font-semibold"
                    style={{ color: accentColor, fontFamily: 'Outfit, sans-serif' }}
                  >
                    {certificate.course_title}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className="p-4 rounded-lg text-center"
                    style={{ backgroundColor: colors.background }}
                  >
                    <Trophy className="h-5 w-5 mx-auto mb-2" style={{ color: accentColor }} />
                    <p className="text-2xl font-bold" style={{ color: colors.textHeading }}>
                      {certificate.score}%
                    </p>
                    <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>
                      Score
                    </p>
                  </div>
                  
                  <div 
                    className="p-4 rounded-lg text-center"
                    style={{ backgroundColor: colors.background }}
                  >
                    <Award className="h-5 w-5 mx-auto mb-2" style={{ color: accentColor }} />
                    <p 
                      className="text-2xl font-bold capitalize"
                      style={{ color: accentColor }}
                    >
                      {certificate.grade}
                    </p>
                    <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>
                      Grade
                    </p>
                  </div>
                </div>

                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: colors.background }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>
                        Issued On
                      </p>
                      <p style={{ color: colors.textHeading }}>
                        {formatDate(certificate.issued_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider" style={{ color: colors.textMuted }}>
                        Certificate ID
                      </p>
                      <p className="font-mono text-sm" style={{ color: colors.textBody }}>
                        {certificate.certificate_number}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center mt-6 text-sm" style={{ color: colors.textMuted }}>
                Issued by <strong style={{ color: colors.textHeading }}>{certificate.organization}</strong>
              </p>
            </>
          ) : (
            <>
              {/* Invalid */}
              <div className="text-center">
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${colors.fail}20` }}
                >
                  <XCircle className="h-10 w-10" style={{ color: colors.fail }} />
                </div>
                
                <h2 
                  className="text-xl font-bold mb-2"
                  style={{ color: colors.fail, fontFamily: 'Outfit, sans-serif' }}
                >
                  Certificate Not Found
                </h2>
                
                <p className="mb-6" style={{ color: colors.textBody }}>
                  {error || 'This certificate could not be verified. It may be invalid or expired.'}
                </p>
                
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  Certificate Number: <code className="font-mono">{certificateNumber}</code>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div 
          className="p-4 text-center border-t"
          style={{ borderColor: colors.border }}
        >
          <Link to="/academy">
            <Button 
              variant="ghost"
              style={{ color: colors.gold }}
            >
              Visit Treppan Academy
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
